import { useMutation } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  AudienceArtifact,
  ProjectorBoardSort,
  ProjectorState,
  RoomThread,
} from "../../shared/types.ts";
import type { RoleAuth } from "./auth.ts";
import type { ProjectorStripActions } from "../components/OnScreen.tsx";
import type { ThreadActions } from "../components/ThreadRow.tsx";
import {
  notificationCopy,
  projectedRepliesVisibilityCopy,
  projectionBoardShownCopy,
  projectionJoinQrShownCopy,
  projectionRemovedCopy,
  projectionThreadShownCopy,
  replyStatusCopy,
  threadEditSavedCopy,
  threadStatusCopy,
  type NotificationMessage,
  type ProjectionToastKind,
} from "./notificationCopy.ts";
import {
  requireMutationResult,
  runStaffAction,
  type StaffActionToastCopy,
} from "./staffNotifications.ts";

type StaffAudienceContext = {
  sessionId: string;
  joinCode: string | null;
  projector: ProjectorState | null;
  threads: RoomThread[];
  artifacts: AudienceArtifact[];
};

/** Maps the unified thread actions onto the existing Convex API.
 *  Status sync to paired suggestions/starters happens server-side in setPostStatus. */
export function useThreadActions(auth: RoleAuth, audience?: StaffAudienceContext | null): ThreadActions {
  const setPostStatus = useMutation(convexApi.audience.setPostStatus);
  const editPost = useMutation(convexApi.audience.editPost);
  const setReplyStatus = useMutation(convexApi.audience.setReplyStatus);
  const setPostCategory = useMutation(convexApi.categories.setPostCategory);
  const recategorizePost = useMutation(convexApi.audience.recategorizePost);
  const projectThread = useMutation(convexApi.projector.projectThread);
  const showDefault = useMutation(convexApi.projector.showDefault);
  const sessionArg = audience?.sessionId ? audience.sessionId as Id<"sessions"> : null;
  const status = (
    t: RoomThread,
    s: "shown" | "answered" | "hidden",
    copy: StaffActionToastCopy,
  ) =>
    runStaffAction(
      () => requireMutationResult(setPostStatus({ auth, postId: t.id as Id<"audiencePosts">, status: s })),
      copy,
    );
  const copy = (success: NotificationMessage, error: NotificationMessage): StaffActionToastCopy => ({
    success,
    error,
  });

  return {
    approve: (t) => status(t, "shown", copy(threadStatusCopy("approved", t.body), notificationCopy.staff.couldNotUpdateStatus)),
    answer: (t) => status(t, "answered", copy(threadStatusCopy("answered", t.body), notificationCopy.staff.couldNotUpdateStatus)),
    reopen: (t) => status(t, "shown", copy(threadStatusCopy("reopened", t.body), notificationCopy.staff.couldNotUpdateStatus)),
    hide: (t) => status(t, "hidden", copy(threadStatusCopy("hidden", t.body), notificationCopy.staff.couldNotUpdateStatus)),
    saveEdit: (t, body) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      runStaffAction(
        () => requireMutationResult(editPost({ auth, postId: t.id as Id<"audiencePosts">, body: trimmed })),
        copy(threadEditSavedCopy(trimmed), notificationCopy.staff.couldNotUpdateText),
      );
    },
    approveReply: (replyId) =>
      runStaffAction(
        () => requireMutationResult(setReplyStatus({
          auth,
          replyId: replyId as Id<"audienceReplies">,
          status: "shown",
        })),
        copy(replyStatusCopy("approved"), notificationCopy.staff.couldNotUpdateReply),
      ),
    hideReply: (replyId) =>
      runStaffAction(
        () => requireMutationResult(setReplyStatus({
          auth,
          replyId: replyId as Id<"audienceReplies">,
          status: "hidden",
        })),
        copy(replyStatusCopy("hidden"), notificationCopy.staff.couldNotUpdateReply),
      ),
    project: (t) =>
      runStaffAction(
        () => requireMutationResult(projectThread({
          auth,
          threadId: t.id as Id<"audiencePosts">,
          showReplies: true,
        })),
        copy(projectionThreadShownCopy(t.body, true), notificationCopy.staff.couldNotUpdateScreen),
      ),
    stopProjection: () => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(showDefault({ auth, sessionId: sessionArg })),
        copy(
          projectionRemovedCopy(projectionKind(audience?.projector), projectionDetail(audience)),
          notificationCopy.staff.couldNotUpdateScreen,
        ),
      );
    },
    setCategory: (t, categoryId) =>
      runStaffAction(
        () => requireMutationResult(setPostCategory({
          auth,
          postId: t.id as Id<"audiencePosts">,
          categoryId: categoryId as Id<"audienceCategories">,
        })),
        copy({ title: "Category updated" }, { title: "Could not update the category" }),
      ),
    recategorize: (t) =>
      runStaffAction(
        () => requireMutationResult(recategorizePost({
          auth,
          postId: t.id as Id<"audiencePosts">,
        })),
        copy({ title: "Re-running AI categorization" }, { title: "Could not re-run AI categorization" }),
      ),
  };
}

export function useProjectorActions(auth: RoleAuth, audience?: StaffAudienceContext | null): ProjectorStripActions {
  const showBoardMutation = useMutation(convexApi.projector.showBoard);
  const showJoinQrMutation = useMutation(convexApi.projector.showJoinQr);
  const showKeyThemesMutation = useMutation(convexApi.projector.showKeyThemes);
  const showKeyThemesBoardMutation = useMutation(convexApi.projector.showKeyThemesBoard);
  const setProjectedRepliesVisible = useMutation(convexApi.projector.setProjectedRepliesVisible);
  const sessionArg = audience?.sessionId ? audience.sessionId as Id<"sessions"> : null;
  const copy = (success: NotificationMessage, error: NotificationMessage): StaffActionToastCopy => ({
    success,
    error,
  });

  return {
    showBoard: (boardSort: ProjectorBoardSort) => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(showBoardMutation({ auth, sessionId: sessionArg, boardSort })),
        copy(projectionBoardShownCopy(boardSortLabel(boardSort)), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showJoinQr: () => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(showJoinQrMutation({ auth, sessionId: sessionArg })),
        copy(projectionJoinQrShownCopy(audience?.joinCode), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showKeyThemes: () => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(showKeyThemesMutation({ auth, sessionId: sessionArg })),
        copy({ title: "Discussion + key themes on screen" }, notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showKeyThemesBoard: () => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(showKeyThemesBoardMutation({ auth, sessionId: sessionArg })),
        copy({ title: "Key themes board on screen" }, notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    setRepliesVisible: (visible: boolean) => {
      if (!sessionArg) return;
      runStaffAction(
        () => requireMutationResult(setProjectedRepliesVisible({ auth, sessionId: sessionArg, visible })),
        copy(projectedRepliesVisibilityCopy(visible, projectedThreadBody(audience)), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
  };
}

function boardSortLabel(sort: ProjectorBoardSort): string {
  switch (sort) {
    case "most_liked":
      return "most upvotes";
    case "most_replied":
      return "most replies";
    case "oldest_unanswered":
    default:
      return "oldest first";
  }
}

function projectedThreadBody(audience: StaffAudienceContext | null | undefined): string | undefined {
  const projector = audience?.projector;
  if (projector?.mode !== "thread" || !projector.projectedThreadId) return undefined;
  return audience?.threads.find((thread) => thread.id === projector.projectedThreadId)?.body;
}

function projectedArtifactBody(audience: StaffAudienceContext | null | undefined): string | undefined {
  const projector = audience?.projector;
  if (projector?.mode !== "artifact" || !projector.projectedArtifactId) return undefined;
  return audience?.artifacts.find((artifact) => artifact.id === projector.projectedArtifactId)?.body;
}

function projectionKind(projector: ProjectorState | null | undefined): ProjectionToastKind {
  if (projector?.mode === "thread") return "thread";
  if (projector?.mode === "join-qr") return "join-qr";
  if (projector?.mode === "artifact") return "artifact";
  return "board";
}

function projectionDetail(audience: StaffAudienceContext | null | undefined): string | undefined {
  const projector = audience?.projector;
  if (projector?.mode === "thread") return projectedThreadBody(audience);
  if (projector?.mode === "artifact") return projectedArtifactBody(audience);
  if (projector?.mode === "board") return `Board sort: ${boardSortLabel(projector.boardSort)}`;
  return undefined;
}
